import { Component, OnInit, OnDestroy } from '@angular/core';
import { Payment, PaymentsListResponse } from '../../payments.models';
import { PaymentsService } from '../../payments.service';
import { DealersService } from '../../../dealers/dealers.service';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { EmployeesService } from '../../../employees/employees.service';
import { Dealer } from '../../../dealers/dealers.model';
import { Company } from '../../../production-inventory/production-inventory.model';
import { EmployeeBasicInfo } from '../../../employees/employees.model';
import { finalize, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar.service';
import { formatDecimalAmount, SnackbarAlert, SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-payments',
  standalone: false,
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.scss',
})
export class PaymentsComponent implements OnInit, OnDestroy {
  payments: Payment[] = [];
  isLoading: boolean = false;
  isExporting: boolean = false;
  totalCount: number = 0;
  isFiltersVisible: boolean = true;

  /**
   * Money totals from the list response, held as the decimal strings the API sent.
   * `pageReceivedAmount` covers the rows on screen and moves on every page turn;
   * `totalReceivedAmount` covers every row the filters match and moves only when the
   * filters change. Both are read off each response: neither is summed client-side
   * from `payments`, and neither is carried over from an earlier page.
   */
  pageReceivedAmount: string | null = null;
  totalReceivedAmount: string | null = null;
  protected currentPage: number = 1;

  // Filters
  searchTerm: string = '';
  selectedDealerId: string | null = null;
  selectedCompanyId: string | null = null;
  selectedEmployeeId: string | null = null;
  selectedPaymentType: string | null = null;
  startDate: string | null = null;
  endDate: string | null = null;

  readonly paymentTypeOptions = [
    { label: 'Cash', value: 'cash' },
    { label: 'Cheque', value: 'cheque' },
    { label: 'UPI', value: 'upi' },
    { label: 'RTGS', value: 'rtgs' },
    { label: 'NEFT', value: 'neft' },
  ];

  // Pagination
  hasNextPage: boolean = false;
  hasPreviousPage: boolean = false;
  pageSize: number = 10;

  // Dropdown data
  dealers: Dealer[] = [];
  companies: Company[] = [];
  employees: EmployeeBasicInfo[] = [];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private paymentsService: PaymentsService,
    private dealersService: DealersService,
    private companyFactoryService: CompanyFactoryService,
    private employeesService: EmployeesService,
    protected router: Router,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.loadDealers();
    this.loadCompanies();
    this.loadEmployees();
    this.loadPayments();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadPayments(): void {
    this.isLoading = true;
    const params: any = {
      page: this.currentPage,
      search: this.searchTerm,
    };

    if (this.selectedDealerId) params.dealer = this.selectedDealerId;
    if (this.selectedEmployeeId) params.employee = this.selectedEmployeeId;
    if (this.startDate) params.start_date = this.startDate;
    if (this.endDate) params.end_date = this.endDate;
    if (this.selectedCompanyId) params.company = this.selectedCompanyId;
    if (this.selectedPaymentType) params.payment_type = this.selectedPaymentType;

    const sub = this.paymentsService
      .list(params)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: PaymentsListResponse) => {
          this.payments = response.results;
          this.totalCount = response.count;
          this.hasNextPage = !!response.next;
          this.hasPreviousPage = !!response.previous;
          this.pageReceivedAmount = response.page_received_amount ?? null;
          this.totalReceivedAmount = response.total_received_amount ?? null;
        },
        error: error => {
          console.error('Error loading payments', error);
        },
      });
    this.subscriptions.add(sub);
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadPayments();
  }

  loadDealers(): void {
    // Assuming list returns all or paginated - if paginated we might need to handle searching dealers effectively,
    // but for now let's just fetch default list.
    const sub = this.dealersService
      .list({ page_size: 100 }) // Fetch more to populate dropdown
      .subscribe({
        next: (response: any) => {
          // Handle both paginated and non-paginated if unsure, but based on service it returns PaginatedResponse likely
          if (response.results) {
            this.dealers = response.results;
          } else if (Array.isArray(response)) {
            this.dealers = response;
          }
        },
      });
    this.subscriptions.add(sub);
  }

  loadCompanies(): void {
    const sub = this.companyFactoryService.listCompanies().subscribe({
      next: (response: Company[]) => {
        this.companies = response;
      },
    });
    this.subscriptions.add(sub);
  }

  loadEmployees(): void {
    const sub = this.employeesService.lighter_list({ team: 'sales' }).subscribe({
      next: (response: EmployeeBasicInfo[]) => {
        this.employees = response;
      },
    });
    this.subscriptions.add(sub);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadPayments();
  }

  onClearFilters(): void {
    this.searchTerm = '';
    this.selectedDealerId = null;
    this.selectedCompanyId = null;
    this.selectedEmployeeId = null;
    this.selectedPaymentType = null;
    this.startDate = null;
    this.endDate = null;
    this.currentPage = 1;
    this.loadPayments();
  }

  exportDetailed(): void {
    this.isExporting = true;

    const params: any = {};
    if (this.selectedDealerId) params.dealer = this.selectedDealerId;
    if (this.selectedEmployeeId) params.employee = this.selectedEmployeeId;
    if (this.startDate) params.start_date = this.startDate;
    if (this.endDate) params.end_date = this.endDate;
    if (this.selectedCompanyId) params.company = this.selectedCompanyId;
    if (this.selectedPaymentType) params.payment_type = this.selectedPaymentType;

    const sub = this.paymentsService
      .exportDetailed(params)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          const blob = response.body!;
          const disposition = response.headers.get('Content-Disposition') ?? '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename =
            match?.[1]?.replace(/['"]/g, '') ||
            `Payments_Report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
        },
      });
    this.subscriptions.add(sub);
  }

  viewDetails(payment: Payment): void {
    this.router.navigate(['/payments', payment.id]);
  }

  // Decimal strings straight from the API: formatted without ever touching a JS float,
  // so the totals agree digit for digit with the rows they sum.
  protected readonly formatDecimalAmount = formatDecimalAmount;
}
